import type { UploadProgress, UploadTask } from './use-uploads';

type UploadToPresignedUrlProps = {
  url: string;
  file: File;
  onProgress?: (progress: UploadProgress) => void;
};

/**
 * Uploads a file to a backend-issued presigned S3 PUT URL. Returns the same
 * { result, cancel } shape as Amplify's uploadData so the upload engine can
 * treat both paths uniformly.
 */
const uploadToPresignedUrl = ({
  url,
  file,
  onProgress,
}: UploadToPresignedUrlProps): UploadTask => {
  const xhr = new XMLHttpRequest();

  const result = new Promise<void>((resolve, reject) => {
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (event) =>
      onProgress?.({
        transferredBytes: event.loaded,
        totalBytes: event.lengthComputable ? event.total : undefined,
      });

    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.onabort = () =>
      reject(new DOMException('Upload cancelled', 'AbortError'));

    xhr.send(file);
  });

  return {
    result,
    cancel: () => xhr.abort(),
  };
};

type GetUploadUrl = (input: {
  key: string;
  contentType: string;
}) => Promise<string>;

type CreatePresignedUploadTaskProps = {
  getUploadUrl: GetUploadUrl;
  key: string;
  file: File;
  onProgress?: (progress: UploadProgress) => void;
};

/**
 * Fetches a presigned URL from the backend, then uploads the file to it.
 * Cancellation aborts whichever stage is in flight.
 */
export const createPresignedUploadTask = ({
  getUploadUrl,
  key,
  file,
  onProgress,
}: CreatePresignedUploadTaskProps): UploadTask => {
  let innerCancel: (() => void) | undefined;
  let cancelled = false;

  const result = (async () => {
    try {
      const url = await getUploadUrl({ key, contentType: file.type });
      if (cancelled) {
        throw new DOMException('Upload cancelled', 'AbortError');
      }
      const task = uploadToPresignedUrl({ url, file, onProgress });
      innerCancel = task.cancel;
      await task.result;
    } catch (err) {
      if (cancelled) {
        throw new DOMException('Upload cancelled', 'AbortError');
      }
      throw err;
    }
  })();

  return {
    result,
    cancel: () => {
      cancelled = true;
      innerCancel?.();
    },
  };
};
